import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {
  IBranch,
  IDivision,
  IHoliday,
  IFeatureOverride,
  IBranchCreatePayload,
  IBranchUpdatePayload,
  IBranchDeletePayload,
  IDivisionCreatePayload,
  IDivisionUpdatePayload,
  IDivisionDeletePayload,
  IHolidayCreatePayload,
  IHolidayUpdatePayload,
  IHolidayDeletePayload,
  IFeatureOverrideSetPayload,
  IFeatureOverrideRemovePayload,
} from "../../../types/api/company-config";
import {
  listBranches,
  createBranch,
  updateBranch,
  deleteBranch,
  listDivisions,
  createDivision,
  updateDivision,
  deleteDivision,
  listHolidays,
  upcomingHolidays,
  createHoliday,
  updateHoliday,
  deleteHoliday,
  listFeatureOverrides,
  setFeatureOverride,
  removeFeatureOverride,
} from "./callbacks";

// ── Branches ──────────────────────────────────────────────────────────────────

export const useBranchList = (
  qData: QData,
  options?: Omit<UseQueryOptions<TResponseWithQData<IBranch[]>, Error>, "queryKey" | "queryFn">,
): UseQueryResult<TResponseWithQData<IBranch[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IBranch[]>, Error>({
    queryKey: [API_ENDPOINTS.BRANCH_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listBranches(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x.id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: {
      data: [],
      success: true,
      message: "",
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useBranchCreate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (data: IBranch) => void;
    onError?: (e: any) => void;
  },
  options: UseMutationOptions<
    TResponse<IBranch>,
    Error,
    IBranchCreatePayload
  > = {},
): UseMutationResult<
  TResponse<IBranch>,
  Error,
  IBranchCreatePayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => (await createBranch(data)).data,

    onSuccess: (res, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.BRANCH_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return old;

          return {
            ...old,
            data: [
              { key: res.data?.id, ...res.data },
              ...old.data,
            ],
            extra: {
              ...old.extra,
              total: (old.extra?.total ?? 0) + 1,
            },
          };
        },
      );

      queryClient.setQueryData(
        [API_ENDPOINTS.BRANCH_DETAIL, res.data?.id],
        res,
      );

      onSuccess?.(res.data as IBranch);
      options?.onSuccess?.(res, ...rest);
    },

    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },

    ...options,
  });
};

export const useBranchUpdate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (data: IBranch) => void;
    onError?: (e: any) => void;
  },
  options: UseMutationOptions<
    TResponse<IBranch>,
    Error,
    IBranchUpdatePayload
  > = {},
): UseMutationResult<
  TResponse<IBranch>,
  Error,
  IBranchUpdatePayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => (await updateBranch(data)).data,

    onSuccess: (res, ...rest) => {
      const updated = res.data as IBranch;

      queryClient.setQueryData(
        [API_ENDPOINTS.BRANCH_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return old;

          return {
            ...old,
            data: old.data.map((b: IBranch) =>
              b.id === updated.id
                ? {
                    ...b,
                    ...updated,
                    key: updated.id,
                  }
                : b,
            ),
          };
        },
      );

      queryClient.setQueryData(
        [API_ENDPOINTS.BRANCH_DETAIL, updated.id],
        res,
      );

      onSuccess?.(updated);
      options?.onSuccess?.(res, ...rest);
    },

    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },

    ...options,
  });
};

export const useBranchDelete = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: () => void;
    onError?: (e: any) => void;
  },
  options: UseMutationOptions<
    TResponse<null>,
    Error,
    IBranchDeletePayload
  > = {},
): UseMutationResult<
  TResponse<null>,
  Error,
  IBranchDeletePayload
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => (await deleteBranch(data)).data,

    onSuccess: (_res, variables, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.BRANCH_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return old;

          return {
            ...old,
            data: old.data.filter(
              (b: IBranch) => b.id !== variables.id,
            ),
            extra: {
              ...old.extra,
              total: Math.max(
                (old.extra?.total ?? 1) - 1,
                0,
              ),
            },
          };
        },
      );

      queryClient.removeQueries({
        queryKey: [API_ENDPOINTS.BRANCH_DETAIL, variables.id],
      });

      onSuccess?.();
      options?.onSuccess?.(_res, variables, ...rest);
    },

    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },

    ...options,
  });
};

// ── Divisions ─────────────────────────────────────────────────────────────────

export const useDivisionList = (
  qData: QData,
  options?: Omit<UseQueryOptions<TResponseWithQData<IDivision[]>, Error>, "queryKey" | "queryFn">,
): UseQueryResult<TResponseWithQData<IDivision[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IDivision[]>, Error>({
    queryKey: [API_ENDPOINTS.DIVISION_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listDivisions(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x.id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: {
      data: [],
      success: true,
      message: "",
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useDivisionCreate = (
  options?: UseMutationOptions<TResponse<IDivision>, Error, IDivisionCreatePayload> & {
    onSuccess?: (data: IDivision) => void;
    onError?: (e: any) => void;
  },
): UseMutationResult<TResponse<IDivision>, Error, IDivisionCreatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IDivision>, Error, IDivisionCreatePayload>({
    mutationFn: async (data) => (await createDivision(data)).data,
    onSuccess: (res) => {
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.DIVISION_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          return { ...old, data: [{ key: res.data?.id, ...res.data }, ...old.data] };
        },
      );
      options?.onSuccess?.(res.data as IDivision);
    },
    onError: options?.onError,
  });
};

export const useDivisionUpdate = (
  options?: UseMutationOptions<TResponse<IDivision>, Error, IDivisionUpdatePayload> & {
    onSuccess?: (data: IDivision) => void;
    onError?: (e: any) => void;
  },
): UseMutationResult<TResponse<IDivision>, Error, IDivisionUpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IDivision>, Error, IDivisionUpdatePayload>({
    mutationFn: async (data) => (await updateDivision(data)).data,
    onSuccess: (res) => {
      const updated = res.data as IDivision;
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.DIVISION_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((d: IDivision) =>
              d.id === updated.id ? { key: updated.id, ...updated } : d,
            ),
          };
        },
      );
      options?.onSuccess?.(updated);
    },
    onError: options?.onError,
  });
};

export const useDivisionDelete = (
  options?: UseMutationOptions<TResponse<null>, Error, IDivisionDeletePayload> & {
    onSuccess?: () => void;
    onError?: (e: any) => void;
  },
): UseMutationResult<TResponse<null>, Error, IDivisionDeletePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<null>, Error, IDivisionDeletePayload>({
    mutationFn: async (data) => (await deleteDivision(data)).data,
    onSuccess: (_res, variables) => {
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.DIVISION_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          return { ...old, data: old.data.filter((d: IDivision) => d.id !== variables.id) };
        },
      );
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

// ── Holidays ──────────────────────────────────────────────────────────────────

export const useHolidayList = (
  qData: QData,
  options?: Omit<UseQueryOptions<TResponseWithQData<IHoliday[]>, Error>, "queryKey" | "queryFn">,
): UseQueryResult<TResponseWithQData<IHoliday[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IHoliday[]>, Error>({
    queryKey: [API_ENDPOINTS.HOLIDAY_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listHolidays(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x.id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: {
      data: [],
      success: true,
      message: "",
      code: 200,
      qData: { page: 1, limit: 50 },
      extra: { page: 1, limit: 50, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useUpcomingHolidays = (
  params?: Record<string, any>,
  options?: Omit<UseQueryOptions<TResponse<IHoliday[]>, Error>, "queryKey" | "queryFn">,
): UseQueryResult<TResponse<IHoliday[]>, Error> => {
  return useQuery<TResponse<IHoliday[]>, Error>({
    queryKey: [API_ENDPOINTS.HOLIDAY_UPCOMING, params],
    queryFn: async () => {
      const res = await upcomingHolidays(params);
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useHolidayCreate = (
  options?: UseMutationOptions<TResponse<IHoliday>, Error, IHolidayCreatePayload> & {
    onSuccess?: (data: IHoliday) => void;
    onError?: (e: any) => void;
  },
): UseMutationResult<TResponse<IHoliday>, Error, IHolidayCreatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IHoliday>, Error, IHolidayCreatePayload>({
    mutationFn: async (data) => (await createHoliday(data)).data,
    onSuccess: (res) => {
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.HOLIDAY_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          return { ...old, data: [{ key: res.data?.id, ...res.data }, ...old.data] };
        },
      );
      options?.onSuccess?.(res.data as IHoliday);
    },
    onError: options?.onError,
  });
};

export const useHolidayUpdate = (
  options?: UseMutationOptions<TResponse<IHoliday>, Error, IHolidayUpdatePayload> & {
    onSuccess?: (data: IHoliday) => void;
    onError?: (e: any) => void;
  },
): UseMutationResult<TResponse<IHoliday>, Error, IHolidayUpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IHoliday>, Error, IHolidayUpdatePayload>({
    mutationFn: async (data) => (await updateHoliday(data)).data,
    onSuccess: (res) => {
      const updated = res.data as IHoliday;
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.HOLIDAY_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((h: IHoliday) =>
              h.id === updated.id ? { key: updated.id, ...updated } : h,
            ),
          };
        },
      );
      options?.onSuccess?.(updated);
    },
    onError: options?.onError,
  });
};

export const useHolidayDelete = (
  options?: UseMutationOptions<TResponse<null>, Error, IHolidayDeletePayload> & {
    onSuccess?: () => void;
    onError?: (e: any) => void;
  },
): UseMutationResult<TResponse<null>, Error, IHolidayDeletePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<null>, Error, IHolidayDeletePayload>({
    mutationFn: async (data) => (await deleteHoliday(data)).data,
    onSuccess: (_res, variables) => {
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.HOLIDAY_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          return { ...old, data: old.data.filter((h: IHoliday) => h.id !== variables.id) };
        },
      );
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

// ── Feature Overrides ─────────────────────────────────────────────────────────

export const useFeatureOverrideList = (
  qData: QData,
  options?: Omit<UseQueryOptions<TResponseWithQData<IFeatureOverride[]>, Error>, "queryKey" | "queryFn">,
): UseQueryResult<TResponseWithQData<IFeatureOverride[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IFeatureOverride[]>, Error>({
    queryKey: [API_ENDPOINTS.FEATURE_OVERRIDE_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listFeatureOverrides(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x.id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: {
      data: [],
      success: true,
      message: "",
      code: 200,
      qData: { page: 1, limit: 50 },
      extra: { page: 1, limit: 50, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useFeatureOverrideSet = (
  options?: UseMutationOptions<TResponse<IFeatureOverride>, Error, IFeatureOverrideSetPayload> & {
    onSuccess?: (data: IFeatureOverride) => void;
    onError?: (e: any) => void;
  },
): UseMutationResult<TResponse<IFeatureOverride>, Error, IFeatureOverrideSetPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IFeatureOverride>, Error, IFeatureOverrideSetPayload>({
    mutationFn: async (data) => (await setFeatureOverride(data)).data,
    onSuccess: (res) => {
      const updated = res.data as IFeatureOverride;
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.FEATURE_OVERRIDE_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          const exists = old.data.find((f: IFeatureOverride) => f.feature_key === updated.feature_key);
          if (exists) {
            return {
              ...old,
              data: old.data.map((f: IFeatureOverride) =>
                f.feature_key === updated.feature_key ? { key: updated.id, ...updated } : f,
              ),
            };
          }
          return { ...old, data: [{ key: updated.id, ...updated }, ...old.data] };
        },
      );
      options?.onSuccess?.(updated);
    },
    onError: options?.onError,
  });
};

export const useFeatureOverrideRemove = (
  options?: UseMutationOptions<TResponse<null>, Error, IFeatureOverrideRemovePayload> & {
    onSuccess?: () => void;
    onError?: (e: any) => void;
  },
): UseMutationResult<TResponse<null>, Error, IFeatureOverrideRemovePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<null>, Error, IFeatureOverrideRemovePayload>({
    mutationFn: async (data) => (await removeFeatureOverride(data)).data,
    onSuccess: (_res, variables) => {
      queryClient.setQueriesData(
        { queryKey: [API_ENDPOINTS.FEATURE_OVERRIDE_LIST] },
        (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.filter((f: IFeatureOverride) => f.feature_key !== variables.feature_key),
          };
        },
      );
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};
